import styles from './AddressBlock.module.css';

export interface AddressBlockProps {
  /**
   * The label, top to bottom, the country last: a doctor's `address.lines`, or
   * `addressLines()` of what a form holds. Written line for line as it is.
   */
  lines: readonly string[];
  /** Who it is for, above the address: a doctor's name. Written in the direction of its own script. */
  name?: string | null;
  /** The clinic or the company, under the name. */
  clinic?: string | null;
  className?: string;
}

/**
 * An address as its country's post writes it: one line under another, in an
 * `<address>`. The request of 6 October 2026 (fifth), section 2: the address
 * is shown in the format of its country everywhere it appears, so everywhere
 * draws it with this, from the lines the server sends.
 *
 * It draws what it is given and decides nothing: which of a doctor's two
 * addresses to show, the one in parts or the text typed before there were
 * parts, is `addressShown()` in the domain, and the old text is not an
 * address in lines and is not for this block.
 *
 * A name and a clinic are typed by people in any script, so each takes the
 * direction of its own letters (`dir="auto"`); the lines of the address are
 * the post's and read as the page does. Nothing here is a link or a control.
 */
export function AddressBlock({ lines, name, clinic, className }: AddressBlockProps) {
  const who = [name, clinic].filter((line): line is string => line != null && line.trim() !== '');
  if (who.length === 0 && lines.length === 0) return null;

  return (
    <address className={className === undefined ? styles.block : `${styles.block} ${className}`}>
      {who.map((line, index) => (
        // The first line of the block is whom it is for, and reads a little heavier.
        <span key={`who-${String(index)}`} className={index === 0 ? styles.first : undefined} dir="auto">
          {line}
        </span>
      ))}
      {lines.map((line, index) => (
        <span key={`line-${String(index)}`}>{line}</span>
      ))}
    </address>
  );
}
